import { useState, useEffect, useRef, useCallback } from 'react';
import { getStoredToken } from '../lib/auth';
import type { BusPosition } from '../lib/types';

interface UseBusTrackerOptions {
  busId: string | null;
  enabled?: boolean;
}

export function useBusTracker({ busId, enabled = true }: UseBusTrackerOptions) {
  const [position, setPosition] = useState<BusPosition | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const connect = useCallback(() => {
    if (!busId || !enabled) return;

    const token = getStoredToken();
    if (!token) {
      setError('Not authenticated');
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/gps/${busId}/ws?token=${token}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setError(null);
      retryCountRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'position') {
          setPosition({
            lat: data.lat,
            lng: data.lng,
            speed: data.speed,
            heading: data.heading,
            accuracy: data.accuracy,
            ts: data.ts,
          });
        }
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;

      // Exponential backoff reconnection (max 10 retries to save battery)
      if (enabled && retryCountRef.current < 10) {
        const delay = Math.min(1000 * 2 ** retryCountRef.current, 30000);
        retryCountRef.current++;
        retryTimerRef.current = setTimeout(connect, delay);
      } else if (retryCountRef.current >= 10) {
        setError('Connection lost. Pull down to refresh.');
      }
    };

    ws.onerror = () => {
      setError('Connection error');
    };
  }, [busId, enabled]);

  useEffect(() => {
    connect();
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Send ping every 30s to keep connection alive
  useEffect(() => {
    if (!connected) return;
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [connected]);

  return { position, connected, error };
}
